import React from 'react';
import Button from './Button';

/**
 * Application queue row component
 * Design: High-contrast, rectangular, action buttons
 */
export default function ApplicationRow({
  application,
  onAccept,
  onDecline,
  onViewCredentials,
  onClick,
  showActions = true,
  className = '',
}) {
  const {
    guest: user,
    createdAt: appliedAt,
    status = 'pending',
    message,
  } = application;

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-500',
    accepted: 'bg-green-100 text-green-800 border-green-500',
    declined: 'bg-red-100 text-red-800 border-red-500',
    waitlisted: 'bg-gray-100 text-gray-800 border-gray-500',
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).toUpperCase();
  };

  const handleUserClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(application);
    }
  };

  return (
    <div className={`border-2 border-black bg-white ${className}`}>
      <div className="p-4">
        {/* User section (avatar + info) */}
        <div
          className={`flex items-center gap-3 ${onClick ? 'cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors' : ''}`}
          onClick={handleUserClick}
        >
          {/* Avatar */}
          <div className="w-12 h-12 flex-shrink-0 border-2 border-black overflow-hidden bg-gray-100">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-mono text-lg font-bold text-black">
                {user?.displayName?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h4 className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                {user?.displayName || 'Unknown User'}
              </h4>
              <span className={`px-2 py-0.5 font-mono text-xs font-semibold uppercase tracking-wider border ${statusColors[status]} flex-shrink-0`}>
                {status}
              </span>
            </div>
            <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
              APPLIED {formatDate(appliedAt)}
            </p>
            {message && (
              <p className="font-mono text-xs text-gray-600 mt-2 line-clamp-2">
                "{message}"
              </p>
            )}
          </div>
        </div>

        {/* Actions — stacked below on mobile */}
        {showActions && status === 'pending' && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 justify-end">
            {onViewCredentials && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewCredentials(application)}
              >
                VIEW
              </Button>
            )}
            {onDecline && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => onDecline(application)}
              >
                DECLINE
              </Button>
            )}
            {onAccept && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onAccept(application)}
              >
                ACCEPT
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Application row header for tables
 */
export function ApplicationRowHeader({ className = '' }) {
  return (
    <div className={`border-2 border-black bg-black text-white px-4 py-3 ${className}`}>
      <div className="flex items-center gap-4">
        <div className="w-12" /> {/* Avatar space */}
        <div className="flex-1 font-mono text-xs font-semibold uppercase tracking-wider">
          APPLICANT
        </div>
        <div className="w-32 font-mono text-xs font-semibold uppercase tracking-wider text-center">
          STATUS
        </div>
        <div className="w-48 font-mono text-xs font-semibold uppercase tracking-wider text-center">
          ACTIONS
        </div>
      </div>
    </div>
  );
}
